import type {
  FetchSingleParameterGroupRequest,
  ParameterGroup,
  UpdateSingleParameterGroupRequest
} from 'Services/ProductManagement/Features/Parameter/types'
import { createBaseApi } from 'Util/ApiClient'
import {
  ALLOWED_TOKENS_CATEGORY,
  CUSTOMER_GROUP_CATEGORY,
  DOMICILE_CLASS_CATEGORY,
  PRODUCT_GROUP_CATEGORY,
  SALES_CHANNEL_CATEGORY
} from 'Util/constants'
import { skipToken } from '@reduxjs/toolkit/query/react'

export const parameterApi = createBaseApi({
  reducerPath: 'parameterApi',
  keepUnusedDataFor: 10,
  tagTypes: ['ParameterGroup']
}).injectEndpoints({
  endpoints: builder => ({
    getSingleParameterGroup: builder.query<ParameterGroup, FetchSingleParameterGroupRequest>({
      query: data => ({
        url: `/api/v1/parameter/${data.pta}/${data.category}/`
      }),
      providesTags: result =>
        result ? [{ type: 'ParameterGroup', id: `${result.pta}/${result.category}` }] : []
    }),
    getProductGroupParameterGroup: builder.query<ParameterGroup, { pta: string }>({
      query: ({ pta }) => ({
        url: `/api/v1/parameter/${pta}/${PRODUCT_GROUP_CATEGORY}/`
      }),
      providesTags: (_result, _error, { pta }) => [
        { type: 'ParameterGroup', id: `${pta}/${PRODUCT_GROUP_CATEGORY}` }
      ]
    }),
    getAllowedTokensParameterGroup: builder.query<ParameterGroup, { pta: string }>({
      query: ({ pta }) => ({
        url: `/api/v1/parameter/${pta}/${ALLOWED_TOKENS_CATEGORY}/`
      }),
      providesTags: (_result, _error, { pta }) => [
        { type: 'ParameterGroup', id: `${pta}/${ALLOWED_TOKENS_CATEGORY}` }
      ]
    }),
    getCustomerGroupParameterGroup: builder.query<ParameterGroup, { pta: string }>({
      query: ({ pta }) => ({
        url: `/api/v1/parameter/${pta}/${CUSTOMER_GROUP_CATEGORY}/`
      }),
      providesTags: (_result, _error, { pta }) => [
        { type: 'ParameterGroup', id: `${pta}/${CUSTOMER_GROUP_CATEGORY}` }
      ]
    }),
    getSalesChannelParameterGroup: builder.query<ParameterGroup, { pta: string }>({
      query: ({ pta }) => ({
        url: `/api/v1/parameter/${pta}/${SALES_CHANNEL_CATEGORY}/`
      }),
      providesTags: (_result, _error, { pta }) => [
        { type: 'ParameterGroup', id: `${pta}/${SALES_CHANNEL_CATEGORY}` }
      ]
    }),
    getDomicileClassParameterGroup: builder.query<ParameterGroup, { pta: string }>({
      query: ({ pta }) => ({
        url: `/api/v1/parameter/${pta}/${DOMICILE_CLASS_CATEGORY}/`
      }),
      providesTags: (_result, _error, { pta }) => [
        { type: 'ParameterGroup', id: `${pta}/${DOMICILE_CLASS_CATEGORY}` }
      ]
    }),
    updateSingleParameterGroup: builder.mutation<ParameterGroup, UpdateSingleParameterGroupRequest>(
      {
        query: data => ({
          url: `/api/v1/parameter/${data.payload.pta}/${data.parameterGroupName}/`,
          method: 'PUT',
          body: data.payload
        }),
        invalidatesTags: (result, _error, data) =>
          result
            ? [{ type: 'ParameterGroup', id: `${result.pta}/${result.category}` }]
            : [
                { type: 'ParameterGroup', id: `${data.payload.pta}/${PRODUCT_GROUP_CATEGORY}` },
                { type: 'ParameterGroup', id: `${data.payload.pta}/${ALLOWED_TOKENS_CATEGORY}` },
                { type: 'ParameterGroup', id: `${data.payload.pta}/${CUSTOMER_GROUP_CATEGORY}` },
                { type: 'ParameterGroup', id: `${data.payload.pta}/${SALES_CHANNEL_CATEGORY}` },
                { type: 'ParameterGroup', id: `${data.payload.pta}/${DOMICILE_CLASS_CATEGORY}` }
              ]
      }
    )
  })
})

/**
 * Helper function to create query argument for parameter group queries,
 * returns skipToken if pta is not provided to prevent the query from running.
 */
export const getPtaQueryParamOrSkip = (pta: string | null | undefined) =>
  pta ? { pta } : skipToken

export const {
  useGetProductGroupParameterGroupQuery,
  useGetAllowedTokensParameterGroupQuery,
  useGetCustomerGroupParameterGroupQuery,
  useGetSalesChannelParameterGroupQuery,
  useGetDomicileClassParameterGroupQuery,
  useGetSingleParameterGroupQuery,
  useUpdateSingleParameterGroupMutation
} = parameterApi
